import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { HERO_TAKE, heroTake } from '../../domain/cars/carSchema';
import { artSource } from '../cars/carArt';
import { ChevronIcon } from '../icons/ControlIcons';
import CarSelectSheet from './CarSelectSheet';

/**
 * Which two cars (SPEC-008 PR-8.5, PR-8.8).
 *
 * Each side is ONE BUTTON — the artwork, the name and a chevron — that opens a
 * sheet of cars. It was a native `<select>` under the artwork, on the argument
 * that a dozen-odd options is exactly the size at which the platform's own picker
 * wins; what that argument missed is that the platform's picker cannot show a
 * CAR. This is a product whose whole proposition is its artwork, and a list of
 * two dozen names in the system font was the one surface where the artwork was
 * replaced by text — on the control a reader touches first.
 *
 * The picture is part of the control rather than a label beside it, because
 * tapping the car is what a reader tries first, and a picture that ignores a tap
 * teaches them the screen is not responsive.
 *
 * The artwork is the HERO take — the side profile, mirrored to face right, which
 * is the same picture and the same orientation the car will have on the strip a
 * few centimetres below. It was briefly the catalogue take, the front view the
 * garage grid draws, on the argument that a car should look like the card it was
 * picked from; that argument loses to this one. On this screen the picker is a
 * LABEL FOR A LANE, and a car drawn head-on above a car drawn in profile reads
 * as two different cars rather than as one being identified.
 *
 * ABOVE 1024 THE ROW BECOMES A CONFRONTATION. The two takes stop being labels
 * for lanes and become the shot this catalogue has been able to draw since the
 * art build shipped a 1536 and a 1920 rung of every car's side profile, and has
 * never once drawn: two cars at five to eight hundred pixels, full bleed, the
 * left one still mirrored to face right and the right one left exactly as it was
 * delivered so it faces left, meeting across the control that races them. It is
 * a composition rather than a size, which is why the mirroring is a rule in
 * `compare.css` keyed to the side rather than a second image.
 *
 * Every car in the catalogue has a side profile today, so `heroTake` falls
 * nobody back — but the Viper and the Testarossa arrived as front views and
 * nothing else for a while, and a front view is drawn face-on and unmirrored,
 * same honest degradation the strip makes. A car pointing at the reader has no
 * direction to get wrong.
 *
 * The same car on both sides is allowed. Every figure ties and the race is a
 * dead heat, which is a legible answer to a question a reader deliberately
 * asked; greying an option out of the other side's list would be a rule the
 * reader has to discover by being refused. The sheet names the other lane's car
 * instead, which tells them without stopping them.
 *
 * There is NO SWAP CONTROL, and there was one. Two pickers already reach every
 * arrangement of two cars, so a third control existed only to save a reader who
 * wanted their own car on the left one trip through a picker — and it cost a
 * permanent target in the middle of the row, on the screen with the most
 * controls in the product. The lanes below are drawn in picker order and that is
 * the whole of what the order decides.
 */

/**
 * The two sides, as data, so nothing below counts to two.
 *
 * `key` is also the class modifier, because above 1024 the two sides are not
 * interchangeable any more: the left car is mirrored to face right and the right
 * one is left as delivered so it faces left, and they meet in the middle. Which
 * side a car is on stops being only an order and becomes a DIRECTION.
 */
const SIDES = [
    { key: 'a', index: 0, labelKey: 'compare.side.a' },
    { key: 'b', index: 1, labelKey: 'compare.side.b' },
];

function PickerSide({ car, index, labelKey, onOpen, side }) {
    const t = useTranslate();
    const art = car ? artSource(car, heroTake(car)) : null;
    const labelId = `compare-side-${index}`;

    return (
        <div className={`compare-picker__side compare-picker__side--${side}`}>
            <p className="compare-picker__label" id={labelId}>{t(labelKey)}</p>

            <button
                type="button"
                className="compare-picker__trigger"
                onClick={() => onOpen(index)}
                // Named by the LANE and by the car in it, in that order, so a
                // reader arriving by tab hears "First, Ferrari F40" rather than
                // a car name with no indication of which side it is on.
                aria-labelledby={`${labelId} ${labelId}-car`}
                aria-haspopup="dialog"
            >
                <span className="compare-picker__art">
                    {art ? (
                        <img
                            className={`compare-picker__image${art.take === HERO_TAKE ? ' compare-picker__image--profile' : ''}`}
                            src={art.src}
                            srcSet={art.srcSet}
                            // EVERY STEP MIRRORS A NAMED RULE IN `compare.css`,
                            // and changing either sheet is one change rather
                            // than two: `sizes` is parsed before CSS resolves,
                            // so a box that widened without this attribute
                            // widening with it fetches a small rung and upscales
                            // — a desktop that looks worse than the phone, with
                            // nothing anywhere reporting it.
                            //
                            // Each figure is the confrontation's side column:
                            // `--stage-max` less two gutters, less the clash
                            // mark and its two gaps, halved. 1680-72-96 over two
                            // is 756 at ULTRA; 1400-72-80 over two is 624 at
                            // BROAD; 1120-72-64 over two is 492 at WIDE. Below
                            // that the shell is still capped at 640 and each
                            // side is about half of it.
                            sizes="(min-width: 1920px) 760px, (min-width: 1440px) 630px, (min-width: 1024px) 500px, (min-width: 640px) 300px, 45vw"
                            // Decorative HERE and only here: the button is
                            // already named by the car, and an alt would have a
                            // screen reader say the name twice.
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    ) : null}
                </span>

                <span className="compare-picker__chosen">
                    <span className="compare-picker__name" id={`${labelId}-car`}>{carName(car)}</span>
                    <ChevronIcon className="compare-picker__chevron" size={16} />
                </span>
            </button>
        </div>
    );
}

export default function ComparePicker({ cars, catalogue, children, onChoose }) {
    const t = useTranslate();
    // Which lane is being filled, or null. ONE piece of state for both sides
    // rather than one each: two sheets can never be open at once, and two
    // booleans would allow the combination that says they are.
    const [openSide, setOpenSide] = useState(null);

    return (
        <section className="compare-picker" aria-label={t('compare.pickerTitle')}>
            <PickerSide
                car={cars[0]}
                index={0}
                labelKey={SIDES[0].labelKey}
                side={SIDES[0].key}
                onOpen={setOpenSide}
            />

            {/* The mark the two cars are pointed at, and the reason it carries
                no letters: a lockup that said VS would be a user-facing string,
                and this product's rule is that every one of those exists in
                every catalogue. Two triangles of the heat ramp meeting nose to
                nose say the same thing in the brand's own material and say it in
                every language. Decorative, and hidden, because the button under
                it already names what happens next.

                Drawn only above 1024 (`compare.css`): between two 45vw cards on
                a phone there is no middle to put anything in. */}
            <span className="compare-picker__clash" aria-hidden="true" />

            <PickerSide
                car={cars[1]}
                index={1}
                labelKey={SIDES[1].labelKey}
                side={SIDES[1].key}
                onOpen={setOpenSide}
            />

            {/* The challenge, handed in by the screen rather than built here.
                It is the screen's state that opens the race, and it belongs
                INSIDE this grid rather than under it because above 1024 the
                pair and the way to race them are one composition — two cars
                facing each other across the control that sets them off. */}
            {children}

            {/* Mounted only while it is open, so `showModal()` runs once per
                opening and the sheet cannot hold a stale lane in its props. */}
            {openSide === null ? null : (
                <CarSelectSheet
                    catalogue={catalogue}
                    chosenId={cars[openSide]?.id ?? null}
                    rivalId={cars[1 - openSide]?.id ?? null}
                    side={openSide}
                    onChoose={(carId) => {
                        onChoose(openSide, carId);
                        setOpenSide(null);
                    }}
                    onClose={() => setOpenSide(null)}
                />
            )}
        </section>
    );
}
